<template>
  <!-- Delete Confirmation Modal -->
  <div class="modal fade" id="deleteItemBlockModal" ref="deleteItemBlockModal" tabindex="-1" role="dialog" aria-hidden="true" data-bs-backdrop="true">
    <div class="modal-dialog" role="document">
      <div class="modal-content" v-if="currentItemBlock">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('page.itemBlock.deleteConfirmTitle') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('page.itemBlock.deleteConfirmMessage', { itemBlockName: currentItemBlock.name, itemBlockCode: currentItemBlock.code }) }}</p>
          <div class="card">
            <div class="card-body">
              <h6 class="card-title">{{ currentItemBlock.code }}</h6>
              <p class="card-text">{{ currentItemBlock.name }}</p>
              <small class="text-muted">{{ $t('field.generic.linesCount') }}: {{ currentItemBlock.partItems.length }}</small>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="confirmDeleteItemBlock" :disabled="isDeleting">
            <div v-if="isDeleting" class="spinner-border spinner-border-sm me-2"></div>
            <font-awesome-icon v-else :icon="['fas', 'trash']" class="me-2" />
            {{ $t('action.delete') }}
          </button>
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" :disabled="isDeleting">
            <font-awesome-icon :icon="['fas', 'xmark']" class="me-2" />
            {{ $t('action.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="container-fluid">
    <div class="row row-cols-1 row-cols-xxl-2">
      <div class="col w-100">
        <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }" :disabled="isSaving">

          <div class="d-flex align-items-center mb-3">
            <div class="p-2">
              <QrCode :text="itemBlockCode" :size="'5rem'" />
            </div>
            <div class="p-2">
              <label for="code" class="form-label">{{ $t('field.itemBlock.code') }}</label>
              <Field id="code" name="code" type="text" class="form-control" :class="{ 'is-invalid': errors.code }" maxlength="40" v-model="itemBlockCode" :disabled="!createMode" />
              <div v-if="itemBlockCodeHasWhitespace" class="form-text text-primary font-monospace whitespace-hint"><template v-for="(seg, i) in itemBlockCodeSegments" :key="i"><span v-if="seg.kind === 'whitespace'" class="text-melodi fw-bold">{{ seg.text }}</span><span v-else-if="seg.kind === 'special'" class="text-danger fw-bold">{{ seg.text }}</span><template v-else>{{ seg.text }}</template></template></div>
              <div class="invalid-feedback" v-if="!!errors.code">{{ errors.code }}</div>
            </div>
            <div class="p-2">
              <label for="name" class="form-label">{{ $t('field.generic.name') }}</label>
              <Field id="name" name="name" type="text" class="form-control" :class="{ 'is-invalid': errors.name }" maxlength="100" v-model="itemBlockName" :disabled="!createMode && !editMode" />
              <div v-if="itemBlockCodeHasWhitespace" class="form-text whitespace-hint">&#8203;</div>
              <div class="invalid-feedback" v-if="!!errors.name">{{ errors.name }}</div>
            </div>

            <!-- Create mode buttons -->
            <div v-if="createMode" class="p-2">
              <button type="submit" class="btn btn-primary" :disabled="isSubmitting || !requiredFieldsComplete">
                <div v-if="isSaving" class="spinner-border spinner-border-sm"></div>
                <font-awesome-icon v-else :icon="['fas', 'floppy-disk']" />
                {{ $t('action.create') }}
              </button>
            </div>

            <!-- View mode buttons (existing item block) -->
            <div v-else-if="!editMode" class="p-2 btn-group">
              <button type="button" class="btn btn-outline-primary" :disabled="isSubmitting" @click="enableEditMode()">
                <font-awesome-icon :icon="['fas', 'pen']" />
                {{ $t('action.edit') }}
              </button>
              <button type="button" class="btn btn-outline-primary" :disabled="isSubmitting" @click="cloneItemBlock()">
                <font-awesome-icon :icon="['fas', 'copy']" />
                {{ $t('action.cloneEllipsis') }}
              </button>
              <button type="button" class="btn btn-outline-danger" :disabled="isSubmitting" @click="showDeleteConfirmation()">
                <font-awesome-icon :icon="['fas', 'trash']" />
                {{ $t('action.delete') }}
              </button>
            </div>

            <!-- Edit mode buttons (existing item block) -->
            <div v-else class="p-2 input-group">
              <button type="button" class="btn btn-outline-secondary" @click="disableEditMode()" :disabled="isSaving">
                <font-awesome-icon :icon="['fas', 'xmark']" />
                {{ $t('action.stopEdit') }}
              </button>
              <span class="input-group-text ms-2">
                <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
              </span>
              <input id="itemFilter" type="search" class="form-control" v-model="itemFilter" :placeholder="$t('action.search')"/>
              <button v-if="itemFilter" type="button" class="btn btn-outline-secondary" @click="clearFilter" :title="$t('action.clear')">
                <font-awesome-icon :icon="['fas', 'filter-slash']" />
              </button>
            </div>
          </div>

          <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>

          <div v-if="!requiredFieldsComplete" class="alert alert-info mt-3 mb-0">
            <font-awesome-icon :icon="['fas', 'info-circle']" />
            <span class="ms-2">{{ $t('instruction.provideRequiredFields') }}</span>
          </div>
          
          <!-- Read-only view (cloning item block in view mode) -->
          <ItemBlockPartList v-else-if="cloningItemBlock" :itemblock-id="cloningItemBlock.id"/>

          <!-- Edit table (create mode or edit mode) -->
          <table v-else-if="editMode">
            <thead>
              <tr>
                <th class="mel-bar"></th>
                <th></th>
                <th>
                  {{ $t('field.generic.item') }}
                  <font-awesome-icon :icon="['fas', 'sort-up']" />
                </th>
                <th>{{ $t('field.generic.quantity') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in filteredItemsList" :key="entry.item.id" :class="{ 'is-filled': entry.isPart }">
                <td class="mel-bar" :class="{ 'mel-bar-primary': entry.isPart }"></td>
                <td @click="toggleItemSelection(entry)"><div class="form-check"><input type="checkbox" class="form-check-input" :value="entry.item.id" :checked="entry.isPart"></div></td>
                <td><ItemField :item="entry.item" /></td>
                <td style="min-width: 140px;">
                  <div v-if="entry.isPart" class="input-group">
                    <input type="number" class="form-control" v-model="entry.quantity" min="0" max="1000" size="4" style="min-width: 32px;" @focus="($event.target as HTMLInputElement).select()">
                    <span class="input-group-text text-secondary">{{ entry.item.baseUnitCode }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Read-only view (existing item block in view mode) -->
          <ItemBlockPartList v-else-if="currentItemBlock" :itemblock-id="currentItemBlock.id"/>

        </Form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onBeforeUnmount, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { Id, toast, type ToastOptions } from 'vue3-toastify';
import * as Yup from 'yup';
import { Form, Field, GenericObject } from 'vee-validate';
import { Modal } from 'bootstrap';
import { useSessionStore, useItemsStore, useItemBlocksStore, useReplacementItemsStore } from '@/stores';
import { router } from '@/services';
import { useI18n } from 'vue-i18n';
import { ItemField } from '@/components/fields';
import IItemBlock from '@/models/responses/IItemBlock';
import UpsertItemBlockRequest, { SyncItemBlockPartRequest } from '@/models/requests/UpsertItemBlockRequest';
import Item from '@/models/frontend/Item';
import QrCode from '@/components/diagram/QrCode.vue';
import ItemBlockPartList from '@/components/masterdata/ItemBlockPartList.vue';

const props = defineProps({
  itemBlockId: { type: Number, required: false, default: null },
  locale: { type: String, required: false, default: 'en' }
});

const currentItemBlock = ref(undefined as IItemBlock | undefined);
const cloningItemBlock = ref(undefined as IItemBlock | undefined);
const itemBlockCode = ref('');
const itemBlockName = ref('');
const createMode = ref(false);
const editMode = ref(false);
const itemFilter = ref('');

class SelectableItem {
  constructor(
    public isPart: boolean,
    public item: Item,
    public quantity: number
  ) { }
}

const sessionStore = useSessionStore();
const itemsStore = useItemsStore();
const itemBlocksStore = useItemBlocksStore();
const replacementItemsStore = useReplacementItemsStore();

const { selectedSiteId } = storeToRefs(sessionStore);
const { data: items } = storeToRefs(itemsStore);

const i18n = useI18n();

const isSaving = ref(false);
const isDeleting = ref(false);
const toastId = ref('' as Id);
const autoSaveInterval = ref<number | null>(null);
const deleteItemBlockModal = ref<HTMLElement | null>(null);

// Track original values to detect changes (for existing item blocks)
const originalCode = ref('');
const originalName = ref('');
const originalQuantities = ref<Map<number, number>>(new Map());
const lastSavedState = ref<string>('');

const schema = Yup.object().shape({
  code: Yup.string().required(i18n.t('validation.required')).max(40, i18n.t('validation.maxLength', { max: 40 })),
  name: Yup.string().required(i18n.t('validation.required')).max(100, i18n.t('validation.maxLength', { max: 100 })),
});

const requiredFieldsComplete = computed(() => itemBlockCode.value !== '' && itemBlockName.value !== '');

const itemBlockCodeHasWhitespace = computed(() => /[^\p{L}\p{N}]/u.test(itemBlockCode.value));
const itemBlockCodeSegments = computed(() => {
  const segments: { text: string; kind: 'text' | 'whitespace' | 'special' }[] = [];
  let buffer = '';
  const flush = () => {
    if (buffer) {
      segments.push({ text: buffer, kind: 'text' });
      buffer = '';
    }
  };
  for (const ch of itemBlockCode.value) {
    if (ch === ' ') {
      flush();
      segments.push({ text: '·', kind: 'whitespace' });
    } else if (ch === '\t') {
      flush();
      segments.push({ text: '→', kind: 'whitespace' });
    } else if (/[^\p{L}\p{N}]/u.test(ch)) {
      flush();
      segments.push({ text: ch, kind: 'special' });
    } else {
      buffer += ch;
    }
  }
  flush();
  return segments;
});

const enableEditMode = () => {
  // Store original values before editing starts
  saveOriginalValues();
  createMode.value = false;
  editMode.value = true;
  startAutoSave();
};

const disableEditMode = () => {
  stopAutoSave();

  // Still perform the auto-submit on unmount
  if (editMode.value && requiredFieldsComplete.value) {
    autoSubmit();
  }

  if (!createMode.value) {
    initData(currentItemBlock.value?.id || 0);
  }
  editMode.value = false;
};

// Save original values for change tracking
const saveOriginalValues = () => {
  if (currentItemBlock.value) {
    originalCode.value = currentItemBlock.value.code;
    originalName.value = currentItemBlock.value.name;
    originalQuantities.value = new Map();
    
    // Save original quantities
    for (const partItem of currentItemBlock.value.partItems) {
      originalQuantities.value.set(partItem.itemId, partItem.quantity);
    }
    
    // Create a serialized state string for quick comparison
    updateLastSavedState();
  }
};

// Update the last saved state after a successful save
const updateLastSavedState = () => {
  // Create a serialized representation of the current state
  const selectedItemsData = selectedItems.value.map(item => ({
    isPart: item.isPart,
    id: item.item.id,
    quantity: item.quantity
  }));
  
  lastSavedState.value = JSON.stringify({
    code: itemBlockCode.value,
    name: itemBlockName.value,
    items: selectedItemsData
  });
};

// Check if current state differs from last saved state
const hasChanges = (): boolean => {
  // If we haven't saved original values yet, assume there are changes
  if (lastSavedState.value === '') return true;
  
  // Create a current state representation
  const selectedItemsData = selectedItems.value.map(item => ({
    isPart: item.isPart,
    id: item.item.id,
    quantity: item.quantity
  }));
  
  const currentState = JSON.stringify({
    code: itemBlockCode.value,
    name: itemBlockName.value,
    items: selectedItemsData
  });
  
  // Compare with last saved state
  return currentState !== lastSavedState.value;
};

const startAutoSave = () => {
  // Only start auto-save for existing item blocks in edit mode
  if (createMode.value) return;
  
  // Clear any existing interval first
  stopAutoSave();

  // Set new interval to auto-save every 10 seconds (10000ms)
  autoSaveInterval.value = window.setInterval(() => {
    if (!isSaving.value && editMode.value && requiredFieldsComplete.value) {
      // Silently auto-save without showing toasts
      silentAutoSave();
    }
  }, 10000);
};

const stopAutoSave = () => {
  if (autoSaveInterval.value !== null) {
    clearInterval(autoSaveInterval.value);
    autoSaveInterval.value = null;
  }
};

const silentAutoSave = async () => {
  if (isSaving.value) return; // Prevent multiple concurrent saves
  
  // Only save if there have been changes
  if (!hasChanges()) return;

  try {
    isSaving.value = true;
    const selectedItemsList = selectedItems.value;
    const selectedUpsertItems = selectedItemsList.map((entry) =>
      new SyncItemBlockPartRequest(entry.item.id, entry.quantity)
    )
    .sort((a, b) => a.itemId - b.itemId); // Sort by itemId to ensure consistent order
    const newOrUpdatedItemBlock = new UpsertItemBlockRequest(
      selectedSiteId.value,
      itemBlockCode.value,
      itemBlockName.value,
      selectedUpsertItems
    );

    // Silent save without displaying toasts
    await itemBlocksStore.upsert(
      newOrUpdatedItemBlock,
      () => {
        isSaving.value = false;
        updateLastSavedState(); // Update last saved state after successful save
      },
      (error) => {
        console.error('Auto-save failed:', error);
        isSaving.value = false;
      }
    );
  } catch (error) {
    console.error('Auto-save failed:', error);
    isSaving.value = false;
  }
};

const onApiFulfilled = (itemBlock: IItemBlock) => {
  isSaving.value = false;
  toast.update(toastId.value, {
    render: i18n.t('statement.itemBlockSaved', { itemBlockCode: itemBlock.code }),
    type: toast.TYPE.SUCCESS,
    isLoading: false,
    autoClose: 10000,
    closeOnClick: true
  } as ToastOptions);
  
  if (createMode.value) {
    // After a successful create, go to edit the item block
    cloningItemBlock.value = undefined; // Clear cloning data
    router.push({ name: 'itemblock', params: { itemBlockId: itemBlock.id, locale: props.locale } });
    enableEditMode();
    initData(itemBlock.id);
  } else {
    // After a successful update, just refresh the data
    editMode.value = false;
    // Reset the change tracking state
    updateLastSavedState();
    initData(itemBlock.id);
    startAutoSave();
  }
};

const onApiRejected = (error: Error) => {
  isSaving.value = false;
  toast.update(toastId.value, {
    render: i18n.t('error.apiError', { error: error.message }),
    closeOnClick: true,
    type: toast.TYPE.ERROR,
    isLoading: false
  } as ToastOptions);
};

const onSubmit = async (values: GenericObject, { setErrors }: GenericObject) => {
  const { code, name } = values;
  try {
    isSaving.value = true;
    toastId.value = toast.loading(i18n.t('status.savingEllipsis'), {
      autoClose: false,
      theme: 'colored'
    } as ToastOptions);
    const selectedUpsertItems = selectedItems.value
      .map((entry) => new SyncItemBlockPartRequest(entry.item.id, entry.quantity))
      .sort((a, b) => a.itemId - b.itemId); // Sort by itemId to ensure consistent order
    const newOrUpdatedItemBlock = new UpsertItemBlockRequest(selectedSiteId.value, code, name, selectedUpsertItems);
    await itemBlocksStore.upsert(newOrUpdatedItemBlock, onApiFulfilled, onApiRejected);
  } catch (error) {
    return setErrors({ apiError: error });
  }
};

const itemsList = ref([] as SelectableItem[]);
const selectedItems = computed(() => itemsList.value.filter((item) => item.isPart));

const filteredItemsList = computed(() => {
  if (!itemFilter.value.trim()) {
    return itemsList.value;
  }
  
  const filterText = itemFilter.value.toLowerCase().trim();
  return itemsList.value.filter(entry => {
    const item = entry.item;
    return (
      item.name.toLowerCase().includes(filterText) ||
      item.code.toLowerCase().includes(filterText) ||
      (item.description && item.description.toLowerCase().includes(filterText))
    );
  });
});

const clearFilter = () => {
  itemFilter.value = '';
};

onMounted(async () => {
  if (selectedSiteId.value === 0) {
    toast.error('No site selected', { autoClose: 10000 });
    return;
  }
  if (items.value.length === 0) {
    toast.error('No items available', { autoClose: 10000 });
    return;
  }

  createMode.value = props.itemBlockId === null;

  if (createMode.value) {
    // Create mode initialization
    initCreateMode();
  } else {
    initData(props.itemBlockId);
    // If already in edit mode on mount, start auto-save
    if (editMode.value) {
      startAutoSave();
    }
  }
});

const initData = (itemBlockId: number) => {
  if (createMode.value || !itemBlockId) return;
  
  const cis = currentItemBlock.value = itemBlocksStore.getById(itemBlockId);
  itemBlockCode.value = cis.code;
  itemBlockName.value = cis.name;

  itemsList.value = [];

  for (const item of items.value) {
    // skip items that are replacements
    if (replacementItemsStore.getBaseItem(item.id) !== undefined) {
      continue;
    }
    if (!item.allowCalculatedLot && (item.trackLotNumber || item.trackBestBeforeDate || item.trackExpirationDate)) {
      continue;
    }
    const partItem = cis.partItems.find((partItem) => partItem.itemId === item.id);
    if (partItem) {
      itemsList.value.push(new SelectableItem(true, item, partItem.quantity));
    } else {
      itemsList.value.push(new SelectableItem(false, item, 0));
    }
  }
  itemsList.value.sort((a, b) => a.item?.name.localeCompare(b.item?.name ?? '') ?? 0);
  
  // Store original values for change tracking
  saveOriginalValues();
};

const initCreateMode = () => {
  itemsList.value = [];

  for (const item of items.value) {
    // skip items that are replacements
    if (replacementItemsStore.getBaseItem(item.id) !== undefined) {
      continue;
    }
    if (!item.allowCalculatedLot && (item.trackLotNumber || item.trackBestBeforeDate || item.trackExpirationDate)) {
      continue;
    }
    const partItem = cloningItemBlock.value?.partItems.find((partItem) => partItem.itemId === item.id);
    if (partItem) {
      itemsList.value.push(new SelectableItem(true, item, partItem.quantity));
    } else {
      itemsList.value.push(new SelectableItem(false, item, 0));
    }
  }
  itemsList.value.sort((a, b) => a.item?.name.localeCompare(b.item?.name ?? '') ?? 0);
};

const autoSubmit = async () => {
  if (editMode.value && requiredFieldsComplete.value) {
    // Only proceed if there are changes to save
    if (hasChanges()) {
      const values = {
        code: itemBlockCode.value,
        name: itemBlockName.value
      };
      // Use a simpler error handler for auto-save
      const setErrors = (errors: any) => {
        console.error('Auto-save failed:', errors);
      };
      await onSubmit(values, { setErrors });
    }
  }
};

const cloneItemBlock = () => {
  if (currentItemBlock.value) {
    // Set the cloning item block and switch to create mode
    cloningItemBlock.value = currentItemBlock.value;
    
    // Clear current item block to switch to create mode
    currentItemBlock.value = undefined;
    
    // Initialize create mode with cloning data
    createMode.value = true;

    // Initialize the base create mode first
    initCreateMode();

    // Then apply cloning data if available
    if (cloningItemBlock.value) {
      // Set the name from the cloned item block
      itemBlockCode.value = i18n.t('global.clone');
      itemBlockName.value = cloningItemBlock.value.name + ' (' + i18n.t('global.clone') + ')';

      // Set the quantities for the items from the cloned item block
      for (const partItem of cloningItemBlock.value.partItems) {
        const item = itemsList.value.find(i => i.item.id === partItem.itemId);
        if (item) {
          item.isPart = true;
          item.quantity = partItem.quantity;
        }
      }

      // focus the code input field
      nextTick(() => {
        const codeInput = document.getElementById('code') as HTMLInputElement | null;
        if (codeInput) {
          codeInput.focus();
          codeInput.select();
        }
      });
    }
  }
};

// Watch for changes in edit mode to start/stop auto-save timer
watch(editMode, (newValue) => {
  if (newValue && !createMode.value) {
    startAutoSave();
  } else {
    stopAutoSave();
  }
});

onBeforeUnmount(() => {
  // Clean up the auto-save interval
  stopAutoSave();

  // Still perform the auto-submit on unmount for existing item blocks
  if (editMode.value && requiredFieldsComplete.value && !createMode.value) {
    autoSubmit();
  }
});

const toggleItemSelection = (entry: SelectableItem) => {
  entry.isPart = !entry.isPart;
};

const showDeleteConfirmation = () => {
  if (currentItemBlock.value && deleteItemBlockModal.value) {
    const modal = new Modal(deleteItemBlockModal.value);
    modal.show();
  }
};

const hideDeleteModal = () => {
  if (deleteItemBlockModal.value) {
    const modal = Modal.getInstance(deleteItemBlockModal.value);
    modal?.hide();
  }
};

const confirmDeleteItemBlock = async () => {
  if (!currentItemBlock.value) return;
  
  try {
    isDeleting.value = true;
    await itemBlocksStore.delete(currentItemBlock.value.id);
    
    // Show success message
    toast.success(i18n.t('page.itemBlock.deletedSuccessfully', { 
      itemBlockCode: currentItemBlock.value.code 
    }), { autoClose: 3000 });
    
    hideDeleteModal();
    
    // Navigate back to the item blocks list (you may need to adjust this route)
    router.push({ name: 'itemblocks' });
    
  } catch (error) {
    console.error('Delete failed:', error);
    toast.error(i18n.t('error.apiError', { error: (error as Error).message }), { 
      autoClose: 10000 
    });
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped type="scss">
input[type=number] {
  text-align: right;
}

.whitespace-hint {
  white-space: pre;
  color: var(--bs-warning-text-emphasis, var(--bs-warning));
}

th {
  font-weight: bold;
}

tr {
  border-bottom: 1px dashed #ccc;

  &:last-child {
    border-bottom: none;
  }
}

th,
td {
  padding-left: .5rem;
  padding-right: .5rem;
}

tr.is-filled > td {
  background-color: var(--mel-row-filled-bg);

  > input {
    background-color: var(--mel-input-highlight-bg);
  }
}

table td.mel-bar,
table th.mel-bar
{
  padding-left: 0;
  padding-right: 0;
  width: 6px;
  min-width: 6px;
  max-width: 6px;
}

.table > :not(caption) > * > .mel-bar-none,
.mel-bar-none {
  background-color: transparent !important;
}
.table > :not(caption) > * > .mel-bar-secondary,
.mel-bar-secondary {
  background-color: var(--bs-body-bg) !important;
}
.table > :not(caption) > * > .mel-bar-primary,
.mel-bar-primary {
  background-color: var(--bs-primary-bg-subtle) !important;
}
.table > :not(caption) > * > .mel-bar-success,
.mel-bar-success {
  background-color: var(--bs-success-bg-subtle) !important;
}
.table > :not(caption) > * > .mel-bar-warning,
.mel-bar-warning {
  background-color: var(--bs-warning-bg-subtle) !important;
}
.table > :not(caption) > * > .mel-bar-danger,
.mel-bar-danger {
  background-color: var(--bs-danger-bg-subtle) !important;
}
</style>
